import { useEffect, useRef, useState } from "react";
import { Play, Pause, Loader2, ChevronUp, ChevronDown } from "lucide-react";
import { useApi } from "../../lib/api";
import { useRadio } from "../../context/RadioContext";

const H = 44;

export const HeaderDial = () => {
  const { data } = useApi("/public/stations");
  const radio = useRadio();
  const wheel = useRef(null);
  const timer = useRef(null);
  const touched = useRef(false);
  const [idx, setIdx] = useState(0);
  const list = (data || []).filter((s) => s.audio_url);
  const cur = list[idx];
  const live = radio.state === "playing" || radio.state === "loading";
  const liveRef = useRef(live);
  liveRef.current = live;

  useEffect(() => {
    const i = list.findIndex((s) => s.key === radio.station?.key);
    if (i < 0 || !wheel.current) return;
    touched.current = true;
    setIdx(i);
    wheel.current.scrollTo({ top: i * H });
  }, [radio.station?.key, list.length]);

  useEffect(() => {
    if (!list.length) return;
    let i = 0;
    const t = setInterval(() => {
      if (touched.current || liveRef.current) return;
      i = (i + 1) % list.length;
      wheel.current?.scrollTo({ top: i * H, behavior: "smooth" });
    }, 1800);
    return () => clearInterval(t);
  }, [list.length]);

  const hold = () => { touched.current = true; };
  const onScroll = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      const i = Math.round(wheel.current.scrollTop / H);
      if (i === idx || !list[i]) return;
      setIdx(i);
      if (liveRef.current && touched.current) radio.play(list[i]);
    }, 160);
  };
  const step = (d) => { hold(); wheel.current.scrollTo({ top: Math.max(0, Math.min(list.length - 1, idx + d)) * H, behavior: "smooth" }); };
  const mine = radio.station?.key === cur?.key;
  const state = mine ? radio.state : "idle";
  const toggle = () => { hold(); state === "playing" || state === "loading" ? radio.stop() : radio.play(cur); };
  if (!list.length) return null;
  const Icon = state === "loading" ? Loader2 : state === "playing" ? Pause : Play;

  return (
    <div className="relative flex items-center rounded-md p-[2px] gold-foil shadow-[1px_1px_0_rgba(70,40,0,.85)]" data-testid="header-radio-dial" title="Scroll to change station">
      <div className="flex items-center bg-[#0e0b04] rounded-[5px]">
        <div className="flex flex-col text-[#f7e08f] px-0.5">
          <button onClick={() => step(-1)} disabled={idx === 0} className="disabled:opacity-25 hover:text-white" aria-label="Previous station" data-testid="header-dial-up"><ChevronUp size={14} /></button>
          <button onClick={() => step(1)} disabled={idx === list.length - 1} className="disabled:opacity-25 hover:text-white" aria-label="Next station" data-testid="header-dial-down"><ChevronDown size={14} /></button>
        </div>
        <div ref={wheel} onScroll={onScroll} onPointerDown={hold} onWheel={hold} onTouchStart={hold} className="relative overflow-y-scroll snap-y snap-mandatory no-scrollbar w-[56px] sm:w-[92px] [mask-image:linear-gradient(transparent,#000_22%,#000_78%,transparent)]" style={{ height: H }} data-testid="header-dial-wheel">
          {list.map((s, i) => (
            <div key={s.key} className={`snap-center flex items-center gap-1.5 px-1 transition-opacity ${i === idx ? "opacity-100" : "opacity-40"}`} style={{ height: H }} data-testid={`header-dial-station-${s.key}`}>
              <img src={s.logo} alt={s.name} className="w-7 h-7 rounded-full object-cover bg-white shrink-0" />
              <span className="font-data text-[10px] leading-tight text-[#f7e08f]">{s.freq}<span className="hidden sm:block text-white/70 truncate">{s.name}</span></span>
            </div>
          ))}
        </div>
        <button onClick={toggle} className="w-8 h-8 m-1 rounded-full gold-foil flex items-center justify-center shrink-0" aria-label={state === "playing" ? "Pause" : `Play ${cur.name}`} data-testid="header-dial-play-btn">
          <Icon size={14} className={`text-[#1b1305] ${state === "loading" ? "animate-spin" : state === "playing" ? "" : "ml-0.5"}`} />
        </button>
      </div>
      {radio.state === "playing" && <span className="live-dot absolute -top-1 -right-1" data-testid="header-dial-live" />}
      {state === "error" && <span className="absolute top-full right-0 mt-1 whitespace-nowrap text-[10px] bg-[#0e0b04] text-red-300 px-2 py-1 rounded" data-testid="header-dial-error">{cur.name} stream is offline at source</span>}
    </div>
  );
};
